import { useMemo, useState, useCallback, useEffect } from "react";
import Pen01 from "../../Nucleo-Icons/pen-01";
import Trash from "../../Nucleo-Icons/trash";
import Box3d50 from "../../Nucleo-Icons/box-3d-50";
import CreateItemModal from "../Information/Item/CreateItemModal";
import SearchIcon from "@mui/icons-material/Search";
import { useParams } from "react-router-dom";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { localizationTextObjectForInformation } from "../../../utils/constants";
import {
  useDeleteData,
  useCreateData,
  usePutData,
  useGetTableData2,
} from "../Plan/tableHooks";
import { useQueryClient } from "@tanstack/react-query";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import * as s from "./styles";
import { Alert, Snackbar } from "@mui/material";
import Confirmation from "../Confirmation";
import { useInsightwinProcessMasterPlan } from "../../../hooks/useInsightwinProcessMasterPlan";
import { AddButton } from "../../common/Buttons";
import goToHelpLink from "../../../utils/goToHelpLink";

function ItemMaster() {
  const { group_code, project_code } = useParams();
  const queryClient = useQueryClient();
  const [clickedItemId, setClickedItemId] = useState(0);
  const [clickedProcId, setClickedProcId] = useState(0);
  const { processMasterData, orderData } = useInsightwinProcessMasterPlan(
    clickedProcId,
    group_code,
    project_code
  );
  const [confirmationConfig, setConfirmationConfig] = useState({
    isOpen: false,
    title: "",
    mainText: "",
    subText: "",
    onConfirm: () => {},
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success', 'error', 'warning', 'info'
  });
  const { data, isLoading, isFetching } = useGetTableData2(
    null,
    `/api/item/getAllItems?group_code=${group_code}&project_code=${project_code}`
  );
  const [updateStatus, setUpdateStatus] = useState(false);
  const [itemSearchText, setItemSearchText] = useState("");
  const openSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };
  const closeSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };
  const handleShowConfirmation = (config) => {
    setConfirmationConfig({
      isOpen: true,
      ...config,
    });
  };
  const handleCloseConfirmation = () => {
    setConfirmationConfig((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };
  const updateItem = usePutData(null, "/api/item/updateItem", openSnackbar);
  const deleteItem = useDeleteData(null, "/api/item/deleteItem", openSnackbar);
  const createItem = useCreateData(null, "/api/item/createItem", openSnackbar);
  const tableData = useMemo(() => {
    if (!data) return [];
    if (!itemSearchText.trim()) return data;
    const lowerCaseSearchText = itemSearchText.toLowerCase();
    return data.filter((item) => {
      return (
        item.item_name?.toLowerCase().includes(lowerCaseSearchText) ||
        String(item.item_id).toLowerCase().includes(lowerCaseSearchText) ||
        String(item.proc_time / 60)
          .toLowerCase()
          .includes(lowerCaseSearchText)
      );
    });
  }, [data, itemSearchText]);
  const columns = [
    {
      accessorKey: "item_id",
      header: "Item ID",
      size: 80,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      enableEditing: false,
    },
    {
      accessorKey: "item_code",
      header: "Item코드",
      size: 80,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
    },
    {
      accessorKey: "item_name",
      header: "Item이름",
      size: 80,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
    },
    {
      accessorKey: "safety_stock",
      header: "안전재고",
      size: 80,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
    },
    {
      accessorKey: "pce",
      header: "PCE",
      size: 80,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      // Cell: ({ row }) => {
      //   return `${Number(row.original.pce) * 100}%`;
      // },
    },
    {
      accessorKey: "lot_size",
      header: "LOT 사이즈",
      size: 80,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
    },
    // {
    //   accessorKey: "proc_qty",
    //   header: "총 공정 수",
    //   size: 80,
    //   muiTableHeadCellProps: { align: "center" },
    //   muiTableBodyCellProps: { align: "center" },
    //   muiTableFooterCellProps: { align: "center" },
    //   enableEditing: false,
    // },
    // {
    //   accessorKey: "proc_time",
    //   header: "공정 시간 합계",
    //   size: 80,
    //   muiTableHeadCellProps: { align: "center" },
    //   muiTableBodyCellProps: { align: "center" },
    //   muiTableFooterCellProps: { align: "center" },
    //   Cell: ({ row }) => {
    //     const minutes = row.original.proc_time / 60;
    //     const isInteger = minutes % 1 === 0;
    //     return `${isInteger ? minutes : minutes.toFixed(1)}분`;
    //   },
    //   enableEditing: false,
    // },
  ];
  const onItemSearchTextChange = useCallback((event) => {
    setItemSearchText(event.target.value);
  }, []);

  const handleDeleteItem = async (row) => {
    const matchedOrder = orderData
      .filter((c) => c.item_id === row.original.item_id)
      .map((c) => c.order_name);
    const mainText = matchedOrder.length
      ? `${matchedOrder} 생산계획이 삭제됩니다.`
      : "아이템을 삭제합니다.";
    handleShowConfirmation({
      title: "아이템 삭제",
      mainText: mainText,
      subText:
        "또한, 하위 공정과 장비 정보가 모두 삭제됩니다. 이 작업은 되돌릴 수 없습니다. 삭제를 진행하시겠습니까?",
      onConfirm: async () => {
        try {
          await deleteItem.mutateAsync({
            itemId: row.original.item_id,
            group_code,
            project_code,
          });
          table.setCreatingRow(null);
          queryClient.invalidateQueries([updateStatus]);
        } catch (error) {
          console.error("Error deleting item:", error);
        }
      },
    });
  };

  const handleEditItem = async ({ values, table, row }) => {
    if (!values.item_name?.trim()) {
      openSnackbar("Item 이름을 입력해 주세요.", "info");
      return;
    }
    if (values.item_name.length > 15) {
      openSnackbar("Item 이름은 15자 이내로 입력해 주세요.", "warning");
      return;
    }
    if (
      values.safety_stock !== undefined &&
      values.safety_stock !== null &&
      values.safety_stock !== ""
    ) {
      const safetyStockValue = parseInt(values.safety_stock);
      if (
        isNaN(safetyStockValue) ||
        safetyStockValue < 0 ||
        safetyStockValue % 1 !== 0
      ) {
        openSnackbar("안전재고는 0 이상의 정수여야 합니다.", "warning");
        return;
      }
    }
    try {
      const updateData = [
        row.original.item_id,
        values.item_code,
        values.item_name,
        values.safety_stock,
        values.pce,
        values.lot_size,
        group_code,
        project_code,
      ];
      await updateItem.mutateAsync(updateData);
      table.setEditingRow(null);
    } catch (error) {
      console.error("Error updating process:", error);
    }
  };
  const handleAddItem = async ({ values, table }) => {
    if (!values.item_name?.trim()) {
      openSnackbar("Item 이름을 입력해 주세요.", "warning");
      return;
    }
    if (values.item_name.length > 15) {
      openSnackbar("Item 이름은 15자 이내로 입력해 주세요.", "warning");
      return;
    }
    if (!values.safety_stock) {
      openSnackbar("안전재고를 입력해 주세요.", "warning");
      return;
    }
    const safetyStockValue = parseInt(values.safety_stock, 10);
    if (
      isNaN(safetyStockValue) ||
      safetyStockValue < 0 ||
      !Number.isInteger(safetyStockValue)
    ) {
      openSnackbar("안전재고는 0 이상의 정수여야 합니다.", "warning");
      return;
    }
    try {
      const createData = [
        values.item_code.trim(),
        values.item_name,
        safetyStockValue,
        String(values.pce),
        values.lot_size,
        group_code,
        project_code,
      ];
      await createItem.mutateAsync(createData);
      table.setCreatingRow(null);
    } catch (error) {
      console.error("Error creating item:", error);
      openSnackbar("Item 생성 중 오류가 발생했습니다.", "error");
    }
  };
  const table = useMaterialReactTable({
    data: tableData ?? [],
    columns,
    enableColumnFilters: false,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableSorting: false,
    enableStickyHeader: true,
    enableColumnActions: false,
    enableEditing: true,
    createDisplayMode: "modal",
    editDisplayMode: "row",
    onCreatingRowSave: handleAddItem,
    localization: localizationTextObjectForInformation,
    initialState: {
      columnVisibility: {
        item_id: false,
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        cursor: "pointer",
        background: () => {
          return clickedItemId === Number(row.original.item_id)
            ? "#dee2e6 !important"
            : "#F8F7FA !important";
        },
      },
      onClick: (e) => {
        setClickedItemId(row.original.item_id);
        setClickedProcId(0);
      },
    }),
    muiTablePaperProps: {
      sx: {
        width: "100%",
        height: "100%",
        background: "#fff",
        "& .css-qc99f7": {
          position: "absolute !important",
          bottom: "30px",
          borderBottom: "1px solid #d6d8df",
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        color: "black",
        fontWeight: "500",
        fontSize: "14px",
      },
    },
    muiTableFooterCellProps: {
      sx: {
        position: "absolute !important",
        bottom: 0,
      },
    },
    onEditingRowSave: handleEditItem,
    muiTableHeadProps: {
      sx: {
        "& tr": {
          fontWeight: "700",
          borderRadius: "14px",
          background: "#f3f6f9",
          color: "#464e5f",
          "& th": {
            fontWeight: "500 !important",
            color: "black",
            padding: "0.5rem",
            fontSize: "15px",
          },
        },
      },
    },
    paginationDisplayMode: "pages",
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          height: "40px",
          width: "150px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          fontSize: "22px",
          background: "#F8F7FA",
          fontWeight: "700",
          gap: "1rem",
          paddingLeft: "0.53rem",
        }}
      >
        <Box3d50 />
        Item{" "}
        <Tooltip
          title="이동"
          sx={{ cursor: "pointer" }}
          onClick={() =>
            goToHelpLink(
              "http://esehelp.cafe24.com/planner#h-8-1-%EC%95%84%EC%9D%B4%ED%85%9C-%EC%98%81%EC%97%AD"
            )
          }
        >
          <HelpOutlineIcon />
        </Tooltip>
      </Box>
    ),
    renderToolbarInternalActions: ({ table }) => (
      <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
        <TextField
          placeholder="검색어를 입력하세요"
          variant="outlined"
          sx={{
            width: "70%",
            marginBottom: "0px",
          }}
          size="small"
          value={itemSearchText}
          onChange={onItemSearchTextChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <AddButton
          onClick={() => {
            table.setCreatingRow(true);
          }}
        />
      </div>
    ),
    positionActionsColumn: "last",
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", width: "50px" }}>
        <Tooltip title="Edit">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <Pen01 />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            onClick={async () => {
              await handleDeleteItem(row);
              queryClient.invalidateQueries([updateStatus]);
            }}
            color="error"
          >
            <Trash />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    renderCreateRowDialogContent: ({ table }) => (
      <CreateItemModal
        table={table}
        handleAddItem={handleAddItem}
        openSnackbar={openSnackbar}
      />
    ),
    muiBottomToolbarProps: {
      sx: {
        position: "absolute !important",
        bottom: "42px",
        left: "50%",
        width: "inherit",
        transform: "translate(-50%, -50%)",
      },
    },
  });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);
  return (
    <>
      <s.ContainerViewer>
        <div
          style={{
            width: "95%",
            display: "flex",
            justifyContent: "center",
            gap: "1.4rem",
            height: "100%",
            alignItems: "flex-start",
          }}
        >
          <s.Section>
            <s.SectionContent>
              <MaterialReactTable
                muiTableBodyProps={{
                  sx: { overflow: "visible" },
                }}
                table={table}
              />
            </s.SectionContent>
          </s.Section>
        </div>
      </s.ContainerViewer>
      <Confirmation
        isOpen={confirmationConfig.isOpen}
        onClose={handleCloseConfirmation}
        onConfirm={confirmationConfig.onConfirm}
        title={confirmationConfig.title}
        mainText={confirmationConfig.mainText}
        subText={confirmationConfig.subText}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ItemMaster;
